import React, { Component } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import './AnnouncementsPage.css';

import GetAnnouncementsActions from '../../../redux/GetAnnouncementsRedux';
import { GetAnnouncementsSelectors } from '../../../redux/GetAnnouncementsRedux';
import SeenAnAnnouncementActions from '../../../redux/SeenAnAnnouncementRedux';

interface Announcement {
  id: string;
  title: string;
  content: string;
  published_at: string;
  is_seen: boolean;
}

interface AnnouncementsPageProps extends WithTranslation {
  navigate: NavigateFunction;
  // Redux props - matching reference project exactly
  getAnnouncementsFetching: boolean;
  getAnnouncementsSuccess: boolean;
  getAnnouncementsError: boolean;
  announcements: Announcement[];
  seenAnAnnouncementFetching: boolean;
  seenAnAnnouncementSuccess: boolean;
  seenAnAnnouncementError: boolean;
  getAnnouncements: (navigate: any) => void;
  seenAnAnnouncement: (announcementId: string) => void;
}

interface AnnouncementsPageState {
  // No state needed for this component
}

class AnnouncementsPage extends Component<AnnouncementsPageProps, AnnouncementsPageState> {
  constructor(props: AnnouncementsPageProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    // Load announcements on mount - matching reference project
    this.props.getAnnouncements(this.props.navigate);
  }



  componentDidUpdate(prevProps: AnnouncementsPageProps) {
    // Match reference project logic exactly
    if (!this.props.seenAnAnnouncementFetching) {
      if (this.props.seenAnAnnouncementSuccess && 
          this.props.seenAnAnnouncementSuccess !== prevProps.seenAnAnnouncementSuccess) {
        this.props.getAnnouncements(this.props.navigate);
      }
    }
  }

  // Match reference project methods exactly
  _onAnnouncementPress = (announcement: any) => {
    this.props.seenAnAnnouncement(announcement.id);
    this.props.navigate(`/announcements/individual/${announcement.id}`);
  };

  _onRefresh = () => {
    this.props.getAnnouncements(this.props.navigate);
  };

  // Helper method to render announcement content with truncation
  renderAnnouncementContent = (content: string, maxLimit: number, t: any) => {
    if (content.length > maxLimit) {
      return (
        <div className="content-truncated">
          <span className="announcement-content">
            {content.substring(0, maxLimit)}
            <span className="announcement-content-see-more">
              {t ? t("announcement-screen:announcement.see-more") : "... see more"}
            </span>
          </span>
        </div>
      );
    }
    
    return (
      <div className="content-full">
        <span className="announcement-content">
          {content}
        </span>
      </div>
    );
  };

  render() {
    const { 
      getAnnouncementsFetching, 
      getAnnouncementsSuccess, 
      announcements,
      t
    } = this.props;

    const maxLimit = 100;



    return (
      <div className="announcements-page">
        {/* Header matching reference project exactly */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <h1 className="header-title">{t ? t("announcement-screen:title") : "Announcements"}</h1>
            </div>
            <div className="header-right"></div>
          </div>
        </div>

        {/* Main content matching reference project exactly */}
        <div className="announcements-container">
          {/* Loading state */}
          {getAnnouncementsFetching && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t ? t("common:loading") : "Loading..."}</p>
            </div>
          )}

          {/* Success state with announcements */}
          {getAnnouncementsSuccess && !getAnnouncementsFetching && Array.isArray(announcements) && announcements.length > 0 && (
            <div className="announcements-list">
              {announcements.map((announcement: any, index: number) => (
                <div 
                  key={index}
                  className={`announcement-item ${announcement.is_seen ? 'seen' : 'unseen'}`}
                  onClick={() => this._onAnnouncementPress(announcement)}
                >
                  <div className="announcement-date">
                    {new Date(announcement.published_at).toLocaleDateString('en-GB')}
                  </div>
                  <div className="announcement-title">
                    {announcement.title}
                  </div>
                  <div className="content-container">
                    {this.renderAnnouncementContent(announcement.content, maxLimit, t)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {getAnnouncementsSuccess && !getAnnouncementsFetching && (!Array.isArray(announcements) || announcements.length === 0) && (
            <div className="empty-state">
              <p>{t ? t("announcement-screen:empty.title") : "No announcements available"}</p>
            </div>
          )}

          {/* Refresh control matching reference project */}
          {/* <div className="refresh-control">
            <button 
              className="refresh-button"
              onClick={this._onRefresh}
              disabled={getAnnouncementsFetching}
            >
              {getAnnouncementsFetching ? (t ? t("common:refreshing") : "Refreshing...") : (t ? t("common:refresh") : "Refresh")}
            </button>
          </div> */}

        </div>
      </div>
    );
  }
}

// Redux connection with optimized state mapping
const mapStateToProps = (state: any) => {
  const getAnnouncements = state.getAnnouncements || {};
  const seenAnAnnouncement = state.seenAnAnnouncement || {};
  
  // Extract announcements safely - handle both array and object with data property
  const announcements = getAnnouncements.announcements;
  const announcementsData = Array.isArray(announcements) ? announcements : (announcements?.data || []);
  
  return {
    getAnnouncementsFetching: getAnnouncements.fetching || false,
    getAnnouncementsSuccess: getAnnouncements.success || false,
    getAnnouncementsError: getAnnouncements.error || false,
    announcements: announcementsData,
    seenAnAnnouncementFetching: seenAnAnnouncement.fetching || false,
    seenAnAnnouncementSuccess: seenAnAnnouncement.success || false,
    seenAnAnnouncementError: seenAnAnnouncement.error || false,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  getAnnouncements: (navigate: any) => dispatch(GetAnnouncementsActions.getAnnouncementsRequest(navigate)),
  seenAnAnnouncement: (announcementId: string) => 
    dispatch(SeenAnAnnouncementActions.seenAnAnnouncementRequest(announcementId)),
});

// i18n connection matching reference project
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AnnouncementsPage));
